import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import anchorme from 'anchorme'
import ReactPlayer from 'react-player'
import debugModule from 'debug'

// evertel
import { DocumentMessageBody, EventMessageBody, ForwardedMessageTile, HoverToolbar, MessageBody, MessageHeader, RepliesCount } from './elements'
import { APIDataReaction, APIDataRoom } from '@evertel/types'
import { useDidMountEffect } from '@evertel/hooks'
import { useService } from '@evertel/di'
import { MessageWallContext, MessageController, MessageReactionsController } from '@evertel/message'
import { Row, Col, RetryButton } from '@evertel/web/ui'
import { ProfilePic, useUserProfile } from '@evertel/web/user'
import { DisplayRoomMessage, DisplayThreadMessage } from '@evertel/stores'
import { CurrentUserController, UserController } from '@evertel/blue-user'
import { ReactionBar } from '@evertel/web/reactions'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { CurrentUserEmojisController } from '@evertel/emojis'
import { localId, positionElementRelativeToTarget } from '@evertel/utils'
import { SessionService } from '@evertel/session/SessionService'
import { NormalizedMedia } from '@evertel/media'

import { MessageBodyProps } from './elements/message/MessageBody'


const debug = debugModule('app:Message')

interface EmojiParam {
    native: APIDataReaction
}
interface MessageProps {
    message: DisplayRoomMessage | DisplayThreadMessage
    modelType: 'room' | 'thread'
    showHoverBar?: boolean // show post hover toolbar (ex. turned off on message search)
    isScrolling?: boolean // informs if item is scrolling in list
    forceHeader?: boolean // for the message header to show always (used in messages search for example)
    highlightString?: string // string searched for in message search (will highlight matching string in post body)
    allowRetracted?: boolean
    variant?: 'list-item' | 'search' | 'reply'

}

const Message: React.FC<MessageProps> = observer(({
    message,
    modelType,
    showHoverBar = true,
    isScrolling = false,
    forceHeader,
    highlightString,
    allowRetracted = false,
    variant = 'list-item'
}) => {

    const {
        setSelectedMessageId,
        setIsReactedByModalOpen,
        setIsForwardMessageModalOpen,
        setEmojiPickerState,
        emojiPickerState,
        modelData, // room data | thread data
        focusedMessageId
    } = useContext(MessageWallContext)

    const ownerId = message.ownerId

    const messageController = useService(MessageController, [message?.id])
    const messageReactionsController = useService(MessageReactionsController, [message?.id])
    const userController = useService(UserController, [ownerId])
    const currentUserController = useService(CurrentUserController, [])
    const daController = useService(DepartmentsAccessController, [ownerId])
    const currentUserEmojisController = useService(CurrentUserEmojisController, [])
    const sessionService = useService(SessionService, [])

    const messageRef = useRef<HTMLDivElement>(null)
    const { openUserProfile } = useUserProfile()

    const [isMouseOver, setIsMouseOver] = useState(false)
    const [isRetracted, setIsRetracted] = useState(false)

    const [isInert, setIsInert] = useState(false)
    const isFocused = focusedMessageId === message.id || isMouseOver

    useState(() => {
        let urls = anchorme.list(message?.text)

        const forwardedText = message?.meta?.forwardedMessage?.text || ''
        if (message.type === 'forwarded' && forwardedText) {
            const forwardedUrls = anchorme.list(forwardedText)
            urls = [...urls, ...forwardedUrls]
        }

        const videoUrls = urls.map(u => {
            if (ReactPlayer.canPlay(u.string)) {
                return u
            }
            return null
        })
            ?.filter(v => v)
            ?.map(v => ({
                id: localId(),
                mediaType: 'video',
                fileName: '',
                mimeType: 'video/*',
                url: v.string,
                isLocalMedia: false,
                width: 1920,
                height: 1080,
                contentLength: 0,
                state: 'remote'
            }))

        userController.init(ownerId)
        daController.init(ownerId)
        messageController.init(message, modelType, videoUrls as NormalizedMedia[])

        messageReactionsController.init(message, modelType)
        //currentUserEmojisController
    })

    useDidMountEffect(() => {
        userController.init(ownerId)
        daController.init(ownerId)
    }, [ownerId])

    useEffect(() => {
        messageController.startUploadMedia()
    }, [])

    useEffect(() => {
        const node = messageRef.current
        if (node && isRetracted) {
            // Capture the current height of the element
            const currentHeight = node.scrollHeight + 'px'
            node.style.height = currentHeight // Set the initial height
            node.style.opacity = '1'

            // Ensure the height is applied before starting the transition
            requestAnimationFrame(() => {
                // this works nicely, but when it re-renders, there's still a jump when it re-renders
                // possibly in the future when virtuoso save state isn't broken, that could be used
                // node.style.height = '0px'
                // node.style.padding = '0px'
                node.style.opacity = '0'
            })
        }
    }, [isRetracted])


    useEffect(() => {
        if (isFocused) {
            setIsInert(false)
        } else {
            setIsInert(true)
        }
    }, [isFocused])

    const onClickHoverToolbarOption = ({ option, messageId, params }: { option: string, messageId: number, params: APIDataReaction }) => {
        switch (option) {
            case 'retract':
                setIsRetracted(true)
                setTimeout(() => {
                    // give time for the animation before retracting
                    messageController.retract()
                }, 500)
                break
            case 'forward-message':
                setSelectedMessageId(message?.id)
                setIsForwardMessageModalOpen(true)
                break
            case 'reaction':
                onSelectReaction(params)
                break
            default:
                break
        }
    }

    const showReactedBy = () => {
        setSelectedMessageId(message?.id)
        setIsReactedByModalOpen(true)
    }

    // NOTE: intentionally NOT memoized. `message` is a MobX store object that is
    // mutated in place (its reference never changes), so `prevOwnerId`/
    // `prevMessageDate` can change underneath us — e.g. when another user's
    // message arrives late and is sorted in above this one. A useMemo keyed on
    // [message] would cache the stale result forever (and stop MobX from
    // tracking these fields), leaving messages grouped under the wrong user
    // until a remount. Computing inline keeps the observer reactive.
    const shouldShowHeader = (() => {
        if (forceHeader) return true

        if (message.type === 'event' || message.isUrgent) {
            return true
        }

        if (!message.prevOwnerId) {
            return true
        }

        if (message.prevOwnerId === message.ownerId) {
            const durationFromPrevPost = moment(message.publishedDate || message.createdDate)
                .diff(moment(message.prevMessageDate), 'minutes')
            return (durationFromPrevPost > 3)
        }
        return true
    })()

    const onSelectReaction = async (reaction: APIDataReaction) => {
        if (variant === 'search') return

        setEmojiPickerState({
            visible: false,
            params: {}
        })

        const emoji = reaction.text

        try {
            const toggleResult = await messageReactionsController.toggleReaction(emoji)
            if (toggleResult === 'added') {
                currentUserEmojisController.incrementFrequent(emoji)
            } else {
                currentUserEmojisController.decrementFrequent(emoji)
            }
        } catch (err) {
            //do nothing
        }
    }

    const onOpenEmojiPicker = (target: string) => {
        positionElementRelativeToTarget('#emoji-picker', target)
        setEmojiPickerState({
            visible: true,
            params: {
                onSelectEmoji: onSelectReaction
            }
        })
    }

    const onMouseEnter = () => {
        setIsMouseOver(true)
    }

    const onMouseLeave = () => {
        if (!emojiPickerState?.visible) {
            setIsMouseOver(false)
        }
    }

    const onRetryPost = async () => {
        await messageController.retryUploadMedia()
    }

    // if (!message) return null

    const user = userController?.user || {}

    const departmentId = (modelData as APIDataRoom)?.departmentId
    const isGuest = (modelType !== 'room' || user?.isBot)
        ? false
        : daController.departmentRole(departmentId) === 'guest'

    const isCurrentUser = ownerId === sessionService.currentUserId
    const hasFailedMedia = messageController?.media.some(m => m.state === 'failed')

    let canRetractTest: boolean
    if (modelType === 'room') {
        canRetractTest = !(modelData as APIDataRoom)?.isArchived
            && (isCurrentUser || currentUserController.canManageInDepartment(departmentId))
    } else if (modelType === 'thread') {
        canRetractTest = isCurrentUser
    }

    //TODO: should most of this go into HoverToolbar?
    const canRetract = canRetractTest && !message.isRetracted
    const canReact = !(modelData as APIDataRoom)?.isArchived && !['search'].includes(variant) && !message.isRetracted
    const canReply = !user?.isBot // not bots // remaining inside hover
    const canForward = (['user', 'user_audio'].includes(message?.type)) && (((modelData as APIDataRoom)?.options as any)?.departmentMembersCanJoinAsMember)

    return (
        <Row
            //key={message.id}
            ref={messageRef}
            aria-roledescription="message"
            {...(!isScrolling) && { onMouseEnter: onMouseEnter }}
            onMouseLeave={onMouseLeave}
            className={classNames(
                'message',
                'post-' + message.id,
                {
                    'no-header': !shouldShowHeader,
                    'hovered': isFocused && showHoverBar,
                    'retracted': message.isRetracted,
                    'unpublished soft-pulse': !message.publishedDate
                }
            )}>
            <div inert={isInert} style={{display: 'contents'}}>
                {/* Profile Pic */}
                <Col
                    className="left-col"
                >
                    {(shouldShowHeader) ? (
                        <button
                            className="p-0 pt-1 m-0"
                            tabIndex={-1}
                            aria-hidden="true"
                            onClick={() => openUserProfile(user?.id)}
                        >
                            <ProfilePic
                                userId={user?.id}
                                isGuest={isGuest}
                            />
                        </button>
                    ):(
                        isFocused && (
                            <div className="message-time-small text-muted">
                                <span style={{fontSize: 11, alignSelf: 'center'}}>
                                    {moment(message.publishedDate || message.createdDate).format('H:mm')}
                                </span>
                            </div>
                        )
                    )}
                </Col>
                <Col className="center-col">
                    <Col valign="top" className="message-body">
                        {/* Message Header */}
                        {(shouldShowHeader) &&
                        <MessageHeader
                            message={message}
                            user={user}
                            isGuest={isGuest}
                            onClickUser={openUserProfile}
                        />
                        }

                        {/* Message Body */}
                        {messageController.message.id && (
                            <div className="overflow-x-auto">
                                <MessageBodyRenderer
                                    type={message.type}
                                    message={messageController?.message}
                                    media={messageController?.media}
                                    highlightString={highlightString}
                                    variant={variant}
                                />
                            </div>
                        )}

                        {/* Reaction Bar */}
                        {(!message.isRetracted && !!messageReactionsController?.message?.reactions?.length) && (
                            <ReactionBar
                                messageId={message.id}
                                reactions={messageReactionsController?.message?.reactions}
                                onSelectReaction={onSelectReaction}
                                onShowEmojiPicker={onOpenEmojiPicker}
                                onShowReactedBy={showReactedBy}
                                showCountLink={!['search'].includes(variant)}
                                showPickerButton={!['search'].includes(variant)}
                                canReact={canReact}
                                emojiButtonId={`emojiButton-${message.id}`}
                            />
                        )}
                    </Col>
                    {/* Replies Bar */}
                    {modelType === 'room' && ((message as DisplayRoomMessage).hasReplies) && variant === 'list-item' && (
                        <Row className="pt-2">
                            <RepliesCount
                                usersCount={message.meta.repliesUsersCount}
                                repliesCount={message.meta.repliesCount}
                                linkTo={`/room/${(modelData as APIDataRoom).id}/replies/${message?.id}`}
                            />
                        </Row>
                    )}
                </Col>
                <Col
                    valign="top"
                    className="right-col"
                >
                    {/* Hover ToolBar */}
                    {(showHoverBar) &&(
                        <HoverToolbar
                            message={message}
                            visible={isFocused}
                            canRetract={canRetract}
                            canReact={canReact}
                            canReply={canReply} //determined inside
                            canForward={canForward}
                            onOpenEmojiPicker={onOpenEmojiPicker}
                            onClickOption={onClickHoverToolbarOption}
                            ownerId={ownerId}
                            variant={variant ?? 'list-item'}
                            user={user}
                        />
                    )}

                    {hasFailedMedia && (
                        <RetryButton
                            postId={message.id}
                            onClick={onRetryPost}
                        />
                    )}
                </Col>
            </div>
        </Row>
    )
})


interface MessageBodyRendererProps extends MessageBodyProps {
    type: string
}

const MessageBodyRenderer = memo<MessageBodyRendererProps>(({
    type,
    message,
    media,
    highlightString,
    variant
}) => {

    switch (type) {
        case 'retracted':
            return <i>This message has been retracted.</i>
        case 'event':
            return message.meta?.eventType === 'document_added' ||
                    message.meta?.eventType === 'document_updated' ? (
                    <DocumentMessageBody message={message} highlightString={highlightString} />
                ) : (
                    <EventMessageBody message={message} />
                )
        case 'forwarded':
            return <ForwardedMessageTile message={message} media={media} variant={variant} />
        case 'user':
        case 'user_audio':
        default:
            return <MessageBody message={message} media={media} highlightString={highlightString} />
    }
})


export { Message }
